import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import { apiRoutes } from "../../../configs/apiRoutes";
import instance from "../../../api/apiClient";
import sanitizeFileName from "helpers/sanitizeFileName";
import { ApiErrorPayload } from "types/ApiErrorPayload";
import { Translation } from "../types";
import {
  getChangedFields,
  normalizeOptionalText,
} from "helpers/getChangedFields";
import { normalizeSeoHtml } from "helpers/seoAttributes";
import type { RootState } from "store";

export interface CollectionItem {
  id: number;
  title: string;
  description: string;
  slug: string;
  isActive: string;
  image: string;
  icon: string;
  countOfGames: number;
  translations: Array<Translation>;
}
export interface CollectionBunchPayload {
  ids: number[];
  isActive: number;
}
interface CollectionPayload {
  id?: number;
  title?: string;
  description?: string;
  slug?: string;
  isActive?: boolean;
  categoryId?: number;
  image?: string | File;
  icon?: string | File;
  translations?: Array<Translation>;
}
interface CollectionsState {
  list: CollectionItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  selectedItems: number[];
  isItemFetching: boolean;
  isItemLocaleFetching: boolean;
  payload?: CollectionPayload;
  initialPayload?: CollectionPayload;
  isItemFetched: boolean;
  processingItemId?: number;
}
interface GetListPayload {
  limit: number;
  offset: number;
  isActive?: number;
  title: string;
  categoryIds: number[];
  order: {
    title: string;
    slug: string;
    countOfGames: string;
  };
}

interface AddGamesPayload {
  gameIds: number[];
  geos: string[];
  collection: number;
}

export const addGamesToCollection = createAsyncThunk(
  "game-center/collections/add-games",
  async (data: AddGamesPayload) => {
    try {
      const result = await instance.put(
        `${apiRoutes.collectionsBase}/${data.collection}/add-games`,
        {
          gameIds: data.gameIds,
          geos: data.geos,
        },
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const getCollectionsList = createAsyncThunk(
  "game-center/collections/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.collectionsList}`, {
        ...data,
      });
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const createCollection = createAsyncThunk<
  number,
  CollectionPayload,
  { rejectValue: ApiErrorPayload }
>(
  "game-center/collections/create",
  async (payload: CollectionPayload, { rejectWithValue }) => {
    const data = new FormData();

    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }

    if (payload.icon instanceof File) {
      const safeName = sanitizeFileName(payload.icon.name);

      data.append(
        "icon",
        new File([payload.icon], safeName, { type: payload.icon.type }),
      );
    }

    data.append("title", (payload?.title as string) ?? "");
    data.append(
      "categoryId",
      (payload?.categoryId?.toString() as string) ?? "",
    );
    data.append("slug", (payload.slug as string) ?? "");
    data.append("description", (payload.description as string) ?? "");
    data.append("isActive", payload.isActive ? "1" : "0");

    try {
      const response = await instance.post(
        `${apiRoutes.collectionCreate}`,
        data,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return response.data.id;
    } catch (error: any) {
      const apiError = error?.data?.error;

      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateCollection = createAsyncThunk<
  boolean,
  CollectionPayload,
  { rejectValue: ApiErrorPayload }
>(
  "game-center/collections/update",
  async (payload: CollectionPayload, { rejectWithValue }) => {
    const data = new FormData();
    if (payload.image instanceof File) {
      const safeName = sanitizeFileName(payload.image.name);

      data.append(
        "image",
        new File([payload.image], safeName, { type: payload.image.type }),
      );
    }

    if (payload.icon instanceof File) {
      const safeName = sanitizeFileName(payload.icon.name);

      data.append(
        "icon",
        new File([payload.icon], safeName, { type: payload.icon.type }),
      );
    }

    data.append("title", payload?.title as string);
    data.append("slug", payload.slug as string);
    data.append("description", payload.description as string);
    data.append("isActive", payload.isActive ? "1" : "0");
    data.append("categoryId", payload?.categoryId?.toString() as string);

    try {
      await instance.put(`${apiRoutes.collectionUpdate}/${payload.id}`, data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return true;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);
export const updateCollectionsBunch = createAsyncThunk(
  "game-center/collections/update-bunch",
  async (data: CollectionBunchPayload) => {
    try {
      await instance.put(`${apiRoutes.updateCollectionsBunch}`, {
        ...data,
        isActive: data.isActive,
      });
      return true;
    } catch (error) {
      return false;
    }
  },
);
export const getCollectionItem = createAsyncThunk(
  "game-center/collections/read",
  async (id: string | number) => {
    try {
      const result = await instance.get(`${apiRoutes.collectionRead}/${id}`);
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);
export const deleteCollectionItem = createAsyncThunk(
  "game-center/collections/delete",
  async (id: string | number) => {
    try {
      const result = await instance.delete(
        `${apiRoutes.collectionDelete}/${id}`,
      );
      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getCollectionItemLocale = createAsyncThunk(
  "game-center/collections/get-item-locale",
  async ({ id, locale }: { id: string | number; locale: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.collectionRead}/${id}/translation/${locale}`,
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

export const updateCollectionItemLocale = createAsyncThunk(
  "game-center/collections/update-item-locale",
  async (
    { id, locale }: { id: string | number; locale: Partial<Translation> },
    { getState },
  ) => {
    try {
      const { locale: localeName, ...restData } = locale;
      const initialTranslation = (
        getState() as RootState
      ).collections.initialPayload?.translations?.find(
        (translation) => translation.locale === localeName,
      );
      const dataToUpdate = getChangedFields(
        initialTranslation,
        restData,
        ["title", "description", "isActive"],
        {
          title: normalizeOptionalText,
          description: normalizeSeoHtml,
          isActive: Boolean,
        },
      );

      await instance.put(
        `${apiRoutes.collectionRead}/${id}/translation/${localeName}`,
        dataToUpdate,
      );
      return true;
    } catch (error) {
      return false;
    }
  },
);

const initialState: CollectionsState = {
  list: [],
  isListFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  isItemFetched: false,
  isItemFetching: false,
  isItemLocaleFetching: false,
};

export const collectionsSlice = createSlice({
  name: "collections",
  initialState,
  reducers: {
    handleUpdateFields: (
      state: CollectionsState,
      action: PayloadAction<CollectionPayload>,
    ) => {
      console.log("action payload   ", action.payload);
      state.payload = { ...state.payload, ...action.payload };
    },
    handleClearFields: (state: CollectionsState) => {
      state.payload = undefined;
      state.initialPayload = undefined;
      state.isItemFetched = false;
    },
    handleInvalidateList: (state: CollectionsState) => {
      state.isListFetched = false;
    },
    handlePaginate: (
      state: CollectionsState,
      action: PayloadAction<number>,
    ) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectItem: (
      state: CollectionsState,
      action: PayloadAction<number>,
    ) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state: CollectionsState) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addGamesToCollection.fulfilled, (state, action) => {
      if (action.payload) {
        state.isListFetched = false;
      }
    });
    builder.addCase(getCollectionsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(createCollection.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createCollection.fulfilled, (state) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createCollection.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateCollection.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateCollection.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image ?? item.image,
              icon:
                action.meta.arg.icon instanceof File
                  ? item.icon
                  : action.meta.arg.icon ?? item.icon,
              isActive: String(action.meta.arg.isActive),
            })
          : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateCollection.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(updateCollectionsBunch.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
    builder.addCase(getCollectionItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.payload = {
        ...action.payload,
        categoryId: action.payload?.category?.id ?? 0,
      };
      state.initialPayload = state.payload
        ? {
            ...state.payload,
            translations: state.payload.translations?.map((translation) => ({
              ...translation,
            })),
          }
        : undefined;
    });
    builder.addCase(deleteCollectionItem.pending, (state, action) => {
      state.processingItemId = +action.meta.arg;
    });

    builder.addCase(deleteCollectionItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteCollectionItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(getCollectionItemLocale.pending, (state) => {
      state.isItemLocaleFetching = true;
    });
    builder.addCase(getCollectionItemLocale.fulfilled, (state, action) => {
      state.isItemLocaleFetching = false;
    });
    builder.addCase(getCollectionItemLocale.rejected, (state, action) => {
      state.isItemLocaleFetching = false;
    });
    builder.addCase(updateCollectionItemLocale.pending, (state) => {
      state.isItemLocaleFetching = true;
      state.isItemFetching = true;
    });
    builder.addCase(updateCollectionItemLocale.fulfilled, (state, action) => {
      state.isItemLocaleFetching = false;
      state.isItemFetching = false;
      state.initialPayload = state.payload
        ? {
            ...state.payload,
            translations: state.payload.translations?.map((translation) => ({
              ...translation,
            })),
          }
        : undefined;
    });
    builder.addCase(updateCollectionItemLocale.rejected, (state, action) => {
      state.isItemLocaleFetching = false;
      state.isItemFetching = false;
    });
  },
});

export const {
  handlePaginate,
  handleSelectItem,
  handleSelectAll,
  handleUpdateFields,
  handleClearFields,
  handleInvalidateList,
} = collectionsSlice.actions;
